import { useEffect, useState, useCallback } from "react";
import { MessageCircleQuestion, ChevronDown, Check, X, Loader2, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import {
  adminListUnanswered,
  adminAnswerUnanswered,
  adminDismissUnanswered,
} from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function QuestionRow({ q, onResolved, onDismissed }) {
  const [answering, setAnswering] = useState(false);
  const [question, setQuestion] = useState(q.question);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Frage und Antwort dürfen nicht leer sein.");
      return;
    }
    setBusy(true);
    try {
      await adminAnswerUnanswered(q.id, { question: question.trim(), answer: answer.trim() });
      onResolved(q.id);
      toast.success("Antwort gespeichert – WALTER kennt sie jetzt.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await adminDismissUnanswered(q.id);
      onDismissed(q.id);
      toast.success("Frage verworfen.");
    } catch {
      toast.error("Verwerfen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg p-3 space-y-2" data-testid={`question-row-${q.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm">{q.question}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {q.user_name || "—"}
              {q.user_email ? ` · ${q.user_email}` : ""}
              {q.user_company ? ` · ${q.user_company}` : ""}
            </span>
            <span>{fmtDate(q.last_asked_at || q.created_at)}</span>
            {q.count > 1 && (
              <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5" data-testid={`question-count-${q.id}`}>
                {q.count}× gefragt
              </span>
            )}
          </div>
        </div>
        {!answering && (
          <div className="flex shrink-0 gap-1">
            <button
              onClick={() => setAnswering(true)}
              className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
              data-testid={`question-answer-btn-${q.id}`}
            >
              Beantworten
            </button>
            <button
              onClick={dismiss}
              disabled={busy}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
              data-testid={`question-dismiss-btn-${q.id}`}
              aria-label="Verwerfen"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {answering && (
        <div className="space-y-2 border-t border-border pt-2" data-testid={`question-answer-form-${q.id}`}>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Frage (für WALTERs FAQ, editierbar)</label>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm font-medium outline-none focus:border-primary"
              data-testid={`question-edit-question-${q.id}`}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Antwort</label>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={3}
              placeholder="Ihre Antwort – wird als WALTER-FAQ gespeichert."
              className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary"
              data-testid={`question-edit-answer-${q.id}`}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
              data-testid={`question-save-${q.id}`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Als FAQ speichern
            </button>
            <button
              onClick={() => {
                setAnswering(false);
                setQuestion(q.question);
                setAnswer("");
              }}
              className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm"
            >
              <X className="h-4 w-4" /> Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WalterQuestionsPanel({ defaultOpen = false }) {
  const [questions, setQuestions] = useState([]);
  const [openPanel, setOpenPanel] = useState(defaultOpen);

  const load = useCallback(async () => {
    try {
      setQuestions(await adminListUnanswered());
    } catch {
      /* still */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = (id) => setQuestions((prev) => prev.filter((x) => x.id !== id));

  return (
    <div className="bg-white border border-border rounded-lg" data-testid="walter-questions-panel">
      <button
        onClick={() => setOpenPanel((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        data-testid="walter-questions-toggle"
      >
        <MessageCircleQuestion className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Fragen an WALTER</h2>
        {questions.length > 0 && (
          <span
            className="text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5"
            data-testid="walter-questions-count"
          >
            {questions.length} offen
          </span>
        )}
        <ChevronDown
          className={`ml-auto h-5 w-5 text-muted-foreground transition-transform ${openPanel ? "rotate-180" : ""}`}
        />
      </button>

      {openPanel && (
        <div className="border-t border-border p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Fragen, die WALTER nicht beantworten konnte. Beantworten Sie eine Frage, wird die Antwort als neuer
            FAQ-Eintrag gespeichert – WALTER kann sie danach automatisch beantworten.
          </p>

          {questions.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="walter-questions-empty">
              Aktuell gibt es keine offenen Fragen. 🎉
            </div>
          ) : (
            questions.map((q) => (
              <QuestionRow key={q.id} q={q} onResolved={remove} onDismissed={remove} />
            ))
          )}
        </div>
      )}
    </div>
  );
}
